import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Layout } from '../components/Layout'
import { Button } from '../components/ui/button'
import { Callout } from '../components/ui/callout'
import { Checkbox } from '../components/ui/checkbox'
import {
    CUSTOM_FIELD_DATA_TYPES,
    CUSTOM_FIELD_ENTITY_OPTIONS,
    customFieldApiError,
    customFieldsApi,
    customFieldValueRequestIsCurrent,
    CustomFieldAddValueModal,
    CustomFieldValueCell,
    fetchEntityNames,
    fieldClass,
    isCustomFieldValueSet,
    useCustomFieldCatalog,
    validateCustomFieldInput,
    type CustomFieldClearedValue,
    type CustomFieldEntityType,
    type CustomFieldValue,
} from '../features/custom-fields'
import { useUser } from '../hooks/useUser'

type WorkingEntity = { id: string; name: string }
type ValuesByEntity = Record<string, Record<string, CustomFieldValue>>

const POSITIVE_DECIMAL = /^[1-9]\d*$/

const isEntityType = (value: string | undefined): value is CustomFieldEntityType =>
    CUSTOM_FIELD_ENTITY_OPTIONS.some((option) => option.value === value)

export const CustomFieldValues = () => {
    const { activeProfile } = useUser()
    const profileId = activeProfile ? String(activeProfile.profile_id) : null

    const params = useParams() as { entityType?: string; definitionId?: string }
    const routeIsScoped = params.entityType !== undefined || params.definitionId !== undefined
    const routeEntityType = isEntityType(params.entityType) ? params.entityType : null
    const routeDefinitionId =
        params.definitionId && POSITIVE_DECIMAL.test(params.definitionId)
            ? params.definitionId
            : null
    const invalidRoute = routeIsScoped && (!routeEntityType || !routeDefinitionId)

    const [entityType, setEntityType] = useState<CustomFieldEntityType>(
        routeEntityType ?? 'campaign',
    )
    useEffect(() => {
        if (routeEntityType) setEntityType(routeEntityType)
    }, [routeEntityType])

    const [addModalOpen, setAddModalOpen] = useState(false)

    const [workingEntities, setWorkingEntities] = useState<WorkingEntity[]>([])
    const workingEntitiesRef = useRef(workingEntities)
    workingEntitiesRef.current = workingEntities
    const [values, setValues] = useState<ValuesByEntity>({})
    const [error, setError] = useState<string | null>(null)
    const [loadingValues, setLoadingValues] = useState(false)
    const latestValueRequest = useRef(0)
    const activeScope = useRef({ profileId, entityType })
    activeScope.current = { profileId, entityType }

    type DiscoveryState = { cursor?: string; exhausted: boolean }
    const [discoveryState, setDiscoveryState] = useState<Record<string, DiscoveryState>>({})
    const [loadingDiscovery, setLoadingDiscovery] = useState(false)
    const latestDiscoveryRequest = useRef(0)

    const [selected, setSelected] = useState<Set<string>>(new Set())
    const [bulkDefinitionId, setBulkDefinitionId] = useState<string | null>(null)
    const [bulkValue, setBulkValue] = useState<string | boolean | null>(null)
    const [bulkSaving, setBulkSaving] = useState(false)
    const [bulkError, setBulkError] = useState<string | null>(null)

    const {
        catalog,
        error: catalogError,
        loading: catalogLoading,
        refresh: refreshCatalog,
    } = useCustomFieldCatalog(profileId, entityType)
    const definitions = routeDefinitionId
        ? (catalog?.definitions ?? []).filter((d) => d.definition_id === routeDefinitionId)
        : (catalog?.definitions ?? [])
    const scopedDefinition = routeDefinitionId ? (definitions[0] ?? null) : null
    const bulkDefinition = definitions.find((d) => d.definition_id === bulkDefinitionId) ?? null

    const loadValuesFor = async (entityIds: string[]) => {
        if (!profileId || entityIds.length === 0) return
        const requestedProfileId = profileId
        const requestedEntityType = entityType
        const request = ++latestValueRequest.current
        const requestIsCurrent = () =>
            customFieldValueRequestIsCurrent(
                request,
                latestValueRequest.current,
                { profileId: requestedProfileId, entityType: requestedEntityType },
                activeScope.current,
            )
        setLoadingValues(true)
        setError(null)
        try {
            const result = await customFieldsApi.values(
                requestedProfileId,
                requestedEntityType,
                entityIds,
            )
            if (!requestIsCurrent()) return
            setValues((current) => ({ ...current, ...result }))
            refreshCatalog()
        } catch (requestError) {
            if (!requestIsCurrent()) return
            setError(customFieldApiError(requestError))
        } finally {
            if (requestIsCurrent()) setLoadingValues(false)
        }
    }

    const hasMoreDiscovery = definitions.some((d) => !discoveryState[d.definition_id]?.exhausted)

    const resolveEntityNames = async (entityIds: string[]) => {
        if (!profileId || entityIds.length === 0) return
        const requestedProfileId = profileId
        const requestedEntityType = entityType
        try {
            const names = await fetchEntityNames(requestedProfileId, requestedEntityType, entityIds)
            if (
                requestedProfileId !== activeScope.current.profileId ||
                requestedEntityType !== activeScope.current.entityType
            )
                return
            setWorkingEntities((current) =>
                current.map((entity) =>
                    names[entity.id] ? { ...entity, name: names[entity.id] } : entity,
                ),
            )
        } catch {
            // Best-effort: leave the raw entity id as the display name.
        }
    }

    const loadDiscoveryPage = async () => {
        if (!profileId || definitions.length === 0) return
        const requestedProfileId = profileId
        const requestedEntityType = entityType
        const request = ++latestDiscoveryRequest.current
        const requestIsCurrent = () =>
            customFieldValueRequestIsCurrent(
                request,
                latestDiscoveryRequest.current,
                { profileId: requestedProfileId, entityType: requestedEntityType },
                activeScope.current,
            )
        const pending = definitions.filter((d) => !discoveryState[d.definition_id]?.exhausted)
        if (pending.length === 0) return
        setLoadingDiscovery(true)
        setError(null)
        try {
            const pages = await Promise.all(
                pending.map((d) =>
                    customFieldsApi
                        .definitionValues(
                            requestedProfileId,
                            requestedEntityType,
                            d.definition_id,
                            discoveryState[d.definition_id]?.cursor,
                        )
                        .then((page) => ({ definitionId: d.definition_id, page })),
                ),
            )
            if (!requestIsCurrent()) return
            const discoveredEntityIds = new Set<string>()
            for (const { page } of pages) {
                for (const value of page.values) discoveredEntityIds.add(value.entity_id)
            }
            const known = new Set(workingEntitiesRef.current.map((entity) => entity.id))
            const newIds = Array.from(discoveredEntityIds).filter((id) => !known.has(id))
            if (newIds.length > 0) {
                const additions = newIds.map((id) => ({ id, name: id }))
                setWorkingEntities((current) => [...current, ...additions])
                void resolveEntityNames(newIds)
            }
            setDiscoveryState((current) => {
                const next = { ...current }
                for (const { definitionId, page } of pages) {
                    next[definitionId] = {
                        cursor: page.next_cursor ?? undefined,
                        exhausted: page.next_cursor === null,
                    }
                }
                return next
            })
            if (discoveredEntityIds.size > 0) void loadValuesFor(Array.from(discoveredEntityIds))
        } catch (requestError) {
            if (!requestIsCurrent()) return
            setError(customFieldApiError(requestError))
        } finally {
            if (requestIsCurrent()) setLoadingDiscovery(false)
        }
    }

    const discoveryStartedFor = useRef<string | null>(null)
    const pendingAddRef = useRef<WorkingEntity | null>(null)

    useEffect(() => {
        latestValueRequest.current++
        latestDiscoveryRequest.current++
        const pending = pendingAddRef.current
        pendingAddRef.current = null
        setWorkingEntities(pending ? [pending] : [])
        setValues({})
        setError(null)
        setLoadingValues(false)
        setLoadingDiscovery(false)
        setDiscoveryState({})
        setSelected(new Set())
        setBulkDefinitionId(null)
        setBulkValue(null)
        discoveryStartedFor.current = null
        if (pending) void loadValuesFor([pending.id])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profileId, entityType, routeDefinitionId])

    const definitionsReady = !catalogLoading && catalog !== null
    useEffect(() => {
        if (!definitionsReady || definitions.length === 0) return
        const scopeKey = `${profileId ?? 'missing'}:${entityType}:${routeDefinitionId ?? ''}`
        if (discoveryStartedFor.current === scopeKey) return
        discoveryStartedFor.current = scopeKey
        void loadDiscoveryPage()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [definitionsReady, definitions.length, profileId, entityType, routeDefinitionId])

    const handleAdded = (entity: WorkingEntity, addedEntityType: CustomFieldEntityType) => {
        if (addedEntityType !== entityType) {
            pendingAddRef.current = entity
            setEntityType(addedEntityType)
            return
        }
        setWorkingEntities((current) =>
            current.some((existing) => existing.id === entity.id) ? current : [...current, entity],
        )
        void loadValuesFor([entity.id])
        refreshCatalog()
    }

    const [removingId, setRemovingId] = useState<string | null>(null)

    const removeEntity = async (id: string) => {
        const entityValues = values[id] ?? {}
        const mutations = definitions
            .filter((d) => entityValues[d.key])
            .map((d) => ({
                field_definition_id: d.definition_id,
                entity_id: id,
                operation: 'clear' as const,
                expected_version: entityValues[d.key]?.version ?? null,
            }))
        if (profileId && mutations.length > 0) {
            setRemovingId(id)
            setError(null)
            try {
                await customFieldsApi.mutateValues(profileId, entityType, mutations)
                refreshCatalog()
            } catch (requestError) {
                setError(customFieldApiError(requestError))
                setRemovingId(null)
                return
            }
            setRemovingId(null)
        }
        setWorkingEntities((current) => current.filter((entity) => entity.id !== id))
        setValues((current) => {
            const next = { ...current }
            delete next[id]
            return next
        })
        setSelected((current) => {
            const next = new Set(current)
            next.delete(id)
            return next
        })
    }

    const toggleSelected = (id: string) => {
        setSelected((current) => {
            const next = new Set(current)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }

    const allSelected = selected.size > 0 && selected.size === workingEntities.length
    const toggleAll = () => {
        setSelected(allSelected ? new Set() : new Set(workingEntities.map((e) => e.id)))
    }

    const applyMutationResults = (results: Array<CustomFieldValue | CustomFieldClearedValue>) => {
        setValues((current) => {
            const next: ValuesByEntity = { ...current }
            for (const item of results) {
                const definition = definitions.find((d) => d.definition_id === item.definition_id)
                if (!definition) continue
                const entityValues = { ...(next[item.entity_id] ?? {}) }
                if (isCustomFieldValueSet(item)) {
                    entityValues[definition.key] = item
                } else {
                    delete entityValues[definition.key]
                }
                next[item.entity_id] = entityValues
            }
            return next
        })
    }

    const applyBulk = async (clear: boolean) => {
        if (!profileId || !bulkDefinition || selected.size === 0) return
        if (!clear) {
            const validation = validateCustomFieldInput(bulkDefinition, bulkValue)
            if (validation) return setBulkError(validation)
        }
        setBulkSaving(true)
        setBulkError(null)
        try {
            const mutations = Array.from(selected).map((entityId) => ({
                field_definition_id: bulkDefinition.definition_id,
                entity_id: entityId,
                operation: (clear ? 'clear' : 'set') as 'clear' | 'set',
                ...(!clear ? { value: bulkValue as string | boolean } : {}),
                expected_version: values[entityId]?.[bulkDefinition.key]?.version ?? null,
            }))
            const result = await customFieldsApi.mutateValues(profileId, entityType, mutations)
            applyMutationResults(result.values)
            setSelected(new Set())
            setBulkValue(null)
            refreshCatalog()
        } catch (requestError) {
            setBulkError(customFieldApiError(requestError))
        } finally {
            setBulkSaving(false)
        }
    }

    const bulkValueInput = useMemo(() => {
        if (!bulkDefinition) return null
        if (bulkDefinition.data_type === CUSTOM_FIELD_DATA_TYPES.Boolean) {
            return (
                <select
                    className={fieldClass}
                    value={bulkValue === null ? '' : bulkValue ? 'true' : 'false'}
                    onChange={(event) =>
                        setBulkValue(
                            event.target.value === '' ? null : event.target.value === 'true',
                        )
                    }
                >
                    <option value="">Not set</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                </select>
            )
        }
        return (
            <input
                className={fieldClass}
                value={typeof bulkValue === 'string' ? bulkValue : ''}
                onChange={(event) => setBulkValue(event.target.value)}
                placeholder={
                    bulkDefinition.data_type === CUSTOM_FIELD_DATA_TYPES.Number ? '0.00' : 'Value'
                }
            />
        )
    }, [bulkDefinition, bulkValue])

    const valueQuotaReached = (catalog?.value_count ?? 0) >= (catalog?.value_limit ?? 100000)
    const entityLabel = CUSTOM_FIELD_ENTITY_OPTIONS.find(
        (option) => option.value === entityType,
    )?.label

    return (
        <Layout
            primary={scopedDefinition ? scopedDefinition.name : 'Custom field values'}
            breadcrumbs={
                routeIsScoped
                    ? [
                          { text: 'Custom fields', href: '/custom-fields' },
                          { text: scopedDefinition?.name ?? 'Values' },
                      ]
                    : [{ text: 'Custom field values' }]
            }
            rightActions={
                !invalidRoute && (
                    <Button onClick={() => setAddModalOpen(true)} disabled={!profileId}>
                        Add value
                    </Button>
                )
            }
        >
            <div className="mx-auto max-w-6xl space-y-5 p-6">
                <Link className="text-sm text-blue-700 underline" to="/custom-fields">
                    Manage definitions
                </Link>

                {invalidRoute && <Callout intent="danger" title="Custom field not found" />}

                {scopedDefinition && (
                    <div className="text-sm text-gray-600">
                        {scopedDefinition.key} · {entityLabel}
                        {scopedDefinition.description && (
                            <p className="mt-2 text-sm text-gray-700">
                                {scopedDefinition.description}
                            </p>
                        )}
                    </div>
                )}

                {!invalidRoute && !scopedDefinition && (
                    <div className="text-sm text-gray-600">Entity type: {entityLabel}</div>
                )}

                <div className="text-sm text-gray-600">
                    {catalog?.definition_count ?? 0}/{catalog?.definition_limit ?? 50} definitions ·{' '}
                    {catalog?.value_count ?? 0}/{catalog?.value_limit ?? 100000} values
                </div>

                {(error || catalogError) && (
                    <Callout intent="danger" title={error ?? catalogError ?? ''} />
                )}
                {!profileId && <Callout intent="warning" title="Select an ad account" />}
                {!invalidRoute && !catalogLoading && definitions.length === 0 && (
                    <Callout intent="warning" title="No custom fields for this entity type yet">
                        Create a definition before setting values.
                    </Callout>
                )}

                {selected.size > 0 && (
                    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[#2d72d2] bg-[#eef4fd] px-3 py-2">
                        <span className="text-sm font-medium text-[#0e2947]">
                            {selected.size} selected
                        </span>
                        {scopedDefinition ? (
                            <span className="text-sm">{scopedDefinition.name}</span>
                        ) : (
                            <select
                                className={fieldClass}
                                value={bulkDefinitionId ?? ''}
                                onChange={(event) => {
                                    setBulkDefinitionId(event.target.value || null)
                                    setBulkValue(null)
                                    setBulkError(null)
                                }}
                            >
                                <option value="" disabled>
                                    Choose a field…
                                </option>
                                {definitions.map((d) => (
                                    <option key={d.definition_id} value={d.definition_id}>
                                        {d.name}
                                    </option>
                                ))}
                            </select>
                        )}
                        {bulkValueInput}
                        <Button
                            size="sm"
                            disabled={bulkSaving || !bulkDefinition || bulkValue === null}
                            onClick={() => applyBulk(false)}
                        >
                            Set for {selected.size}
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={bulkSaving || !bulkDefinition}
                            onClick={() => applyBulk(true)}
                        >
                            Clear for {selected.size}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
                            Cancel
                        </Button>
                        {bulkError && <span className="text-xs text-red-600">{bulkError}</span>}
                    </div>
                )}

                {(loadingValues || loadingDiscovery) && (
                    <div className="text-sm text-gray-500">Loading values…</div>
                )}

                {!invalidRoute && workingEntities.length > 0 && definitions.length > 0 && (
                    <div className="overflow-x-auto rounded border border-gray-200">
                        <table className="min-w-full text-left text-sm">
                            <thead className="border-b border-gray-200 bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-600">
                                <tr>
                                    <th className="w-10 px-3 py-2">
                                        <Checkbox checked={allSelected} onChange={toggleAll} />
                                    </th>
                                    <th className="px-4 py-2">Entity</th>
                                    {definitions.map((d) => (
                                        <th key={d.definition_id} className="px-4 py-2">
                                            {d.name}
                                        </th>
                                    ))}
                                    <th className="w-10 px-3 py-2" />
                                </tr>
                            </thead>
                            <tbody>
                                {workingEntities.map((entity) => (
                                    <tr
                                        key={entity.id}
                                        className={`border-b border-gray-100 last:border-b-0 ${
                                            selected.has(entity.id) ? 'bg-[#f4f8fe]' : ''
                                        }`}
                                    >
                                        <td className="px-3 py-2">
                                            <Checkbox
                                                checked={selected.has(entity.id)}
                                                onChange={() => toggleSelected(entity.id)}
                                            />
                                        </td>
                                        <td
                                            className="max-w-[280px] truncate px-4 py-2"
                                            title={entity.name}
                                        >
                                            {entity.name}
                                        </td>
                                        {definitions.map((d) => (
                                            <td key={d.definition_id} className="px-4 py-2">
                                                <CustomFieldValueCell
                                                    profileId={profileId ?? ''}
                                                    entityType={entityType}
                                                    entityId={entity.id}
                                                    definition={d}
                                                    current={values[entity.id]?.[d.key]}
                                                    valueQuotaReached={valueQuotaReached}
                                                    onChanged={(next) =>
                                                        setValues((current) => {
                                                            const entityValues = {
                                                                ...(current[entity.id] ?? {}),
                                                            }
                                                            if (next) entityValues[d.key] = next
                                                            else delete entityValues[d.key]
                                                            return {
                                                                ...current,
                                                                [entity.id]: entityValues,
                                                            }
                                                        })
                                                    }
                                                />
                                            </td>
                                        ))}
                                        <td className="px-3 py-2">
                                            <button
                                                type="button"
                                                aria-label={`Remove ${entity.name}`}
                                                className="text-xs text-gray-400 hover:text-gray-600 disabled:opacity-50"
                                                disabled={removingId === entity.id}
                                                onClick={() => void removeEntity(entity.id)}
                                            >
                                                {removingId === entity.id ? 'Removing…' : 'Remove'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {hasMoreDiscovery && workingEntities.length > 0 && (
                    <Button
                        variant="outline"
                        disabled={loadingDiscovery}
                        onClick={loadDiscoveryPage}
                    >
                        {loadingDiscovery ? 'Loading…' : 'Load more'}
                    </Button>
                )}

                {!invalidRoute &&
                    workingEntities.length === 0 &&
                    !loadingDiscovery &&
                    definitions.length > 0 && (
                        <div className="rounded border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500">
                            No values set yet. Click "Add value" above to set one.
                        </div>
                    )}
            </div>

            <CustomFieldAddValueModal
                isOpen={addModalOpen}
                onClose={() => setAddModalOpen(false)}
                profileId={profileId}
                lockedEntityType={routeEntityType}
                defaultEntityType={entityType}
                onAdded={handleAdded}
            />
        </Layout>
    )
}
